import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { getCountryConfig } from '../lib/country-config'
import type { VerificationResult } from '../types'
import GlobalAuditTimeline from '../components/AuditTimeline'

export default function Verify() {
  const { t } = useTranslation()
  const { code: urlCode } = useParams<{ code: string }>()

  const [code, setCode] = useState(urlCode || '')
  const [result, setResult] = useState<VerificationResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searched, setSearched] = useState(false)

  const doVerify = useCallback(async (verifyCode: string) => {
    if (!verifyCode.trim()) return
    setError('')
    setResult(null)
    setLoading(true)
    setSearched(true)

    try {
      const res = await api.get<VerificationResult>(`/api/verify/${verifyCode.trim()}`)
      setResult(res)
    } catch (err: unknown) {
      const status = (err && typeof err === 'object' && 'status' in err) ? (err as { status: number }).status : 0
      const body = (err && typeof err === 'object' && 'body' in err) ? (err as { body: any }).body : null

      if (status === 404) {
        setResult({ valid: false })
      } else if (status === 403 && body?.error === 'verification_disabled') {
        // Tenant desabilitou verify pública (NDA/compliance).
        // Mostrar mensagem clara em vez de erro genérico.
        setError(
          body.message ||
          t('verify.disabled', 'A verificação pública deste documento foi desabilitada pelo emissor. Entre em contato com quem enviou o documento.')
        )
      } else {
        setError(t('verify.error', 'Failed to verify. Please try again.'))
      }
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (urlCode) {
      doVerify(urlCode)
    }
  }, [urlCode, doVerify])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    doVerify(code)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="text-xl font-bold text-indigo-600">
            PAKT
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('verify.title', 'Verify Document')}
          </h1>
          <p className="text-gray-600">
            {t('verify.subtitle', 'Enter the verification code to check document authenticity.')}
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8">
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('verify.placeholder', 'Verification code')}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition font-mono"
            />
            <button
              type="submit"
              disabled={loading || !code.trim()}
              className="px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition"
            >
              {loading ? '...' : t('verify.submit', 'Verify')}
            </button>
          </div>
        </form>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm text-center">
            {error}
          </div>
        )}

        {/* Results */}
        {searched && !loading && result && (
          <div className="space-y-6">
            {/* Valid/Invalid Badge */}
            <div className="text-center">
              {result.valid ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full font-semibold">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('verify.valid', 'Valid Document')}
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-full font-semibold">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('verify.invalid', 'Invalid or Not Found')}
                </div>
              )}
            </div>

            {result.valid && result.document && (
              <>
                {/* Document Info */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('verify.docInfo', 'Document Information')}
                  </h2>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">{t('dashboard.name', 'Name')}</dt>
                      <dd className="text-sm font-medium text-gray-900 mt-0.5">
                        {result.document.nome}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">{t('verify.status', 'Status')}</dt>
                      <dd className="mt-0.5">
                        <span
                          className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                            result.document.status === 'COMPLETED'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {result.document.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">{t('verify.createdAt', 'Created on')}</dt>
                      <dd className="text-sm text-gray-900 mt-0.5">
                        {new Date(result.document.createdAt).toLocaleString()}
                      </dd>
                    </div>
                    {result.document.completedAt && (
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase">{t('verify.completedAt', 'Completed on')}</dt>
                        <dd className="text-sm text-gray-900 mt-0.5">
                          {new Date(result.document.completedAt).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Signers */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('verify.signers', 'Signers')}
                  </h2>
                  <div className="space-y-3">
                    {result.document.signers.map((signer, i) => {
                      const signerCountry = getCountryConfig(signer.countryCode)
                      return (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{signer.nome}</span>
                              {signerCountry && <span className="text-sm">{signerCountry.flag}</span>}
                            </div>
                            <p className="text-xs text-gray-500">{signer.email}</p>
                          </div>
                          <div className="sm:text-right">
                            <span
                              className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                                signer.status === 'COMPLETED'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {signer.status}
                            </span>
                            {signer.signedAt && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(signer.signedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Audit Trail */}
                {result.auditLog && result.auditLog.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('verify.audit', 'Audit Trail')}
                    </h2>
                    <GlobalAuditTimeline events={(result.auditLog || []).map(e => ({
                      evento: e.evento,
                      createdAt: e.createdAt || '',
                      metadataJson: e.metadataJson,
                    }))} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {searched && !loading && !result && !error && (
          <div className="text-center text-gray-500">
            <p>{t('common.noResults', 'No results found')}</p>
          </div>
        )}
      </main>
    </div>
  )
}
